// src/pages/OddsMovementPage.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";

// ---------- endpoints ----------
const TOP_MOVERS_ENDPOINTS = ["/api/odds-tracker/movers"];
const CARD_ENDPOINTS = ["/api/public/odds-history/card"]; // <-- your new "all lines" endpoint
const HISTORY_ENDPOINTS = ["/api/odds-tracker/fixture"]; // /api/odds-tracker/fixture/{fixture_id}

// ---------- utils ----------
const todayISO = () => new Date().toISOString().slice(0, 10);

const toUK = (iso, { withZone = false } = {}) => {
  if (!iso) return "—";
  const fmt = { timeZone: "Europe/London", hour: "2-digit", minute: "2-digit" };
  if (withZone) fmt.timeZoneName = "short";
  try {
    const d = new Date(iso);
    if (!isNaN(d)) return d.toLocaleTimeString("en-GB", fmt);
  } catch {}
  try {
    const safe = iso.endsWith("Z") || iso.includes("+") ? iso : iso + "Z";
    return new Date(safe).toLocaleTimeString("en-GB", fmt);
  } catch {
    return iso.slice(11, 16);
  }
};

const num = (x, d = 2) => (typeof x === "number" && isFinite(x) ? x.toFixed(d) : "—");
const safeNum = (x) => (typeof x === "number" && isFinite(x) ? x : null);

async function firstWorkingGet(paths, params) {
  let lastErr = null;
  for (const p of paths) {
    try {
      const { data } = await api.get(p, { params });
      return { data, path: p };
    } catch (e) {
      lastErr = e;
    }
  }
  throw lastErr || new Error("No endpoints available");
}

const summarizeFixture = (lines, minMovePts = 0.1) => {
  let biggest = null;

  for (const l of lines) {
    const open = safeNum(l.open_price);
    const cur = safeNum(l.current_price);
    const mv = safeNum(l.move ?? (open != null && cur != null ? cur - open : null));
    if (mv == null) continue;

    const abs = Math.abs(mv);
    if (!biggest || abs > biggest.abs) {
      biggest = { abs, move: mv, market: l.market, open, cur };
    }
  }

  const moversCount = lines.filter((l) => {
    const mv = safeNum(l.move);
    return mv != null && Math.abs(mv) >= minMovePts;
  }).length;

  return { linesCount: lines.length, moversCount, biggest };
};

const movementTone = (open, cur) => {
  if (typeof open !== "number" || typeof cur !== "number") return "neutral";
  if (cur > open) return "up";
  if (cur < open) return "down";
  return "neutral";
};

const delta = (open, cur) => {
  if (typeof open !== "number" || typeof cur !== "number") return null;
  return cur - open;
};

const routeForFixture = (fixtureId, sport) => {
  const s = (sport || "football").toLowerCase();
  if (s === "rugby" || s === "rugby union") return `/rugby/match/${fixtureId}`;
  if (s === "nba") return `/basketball/game/${fixtureId}`;
  if (s === "nhl") return `/nhl/game/${fixtureId}`;
  if (s === "cfb") return `/cfb/fixture/${fixtureId}`;
  if (s === "nfl") return `/nfl`;
  return `/fixture/${fixtureId}`;
};

// ---------- styles ----------
const S = {
  page: { maxWidth: 1480, margin: "0 auto", padding: 14, fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace" },
  headerRow: {
    position: "sticky",
    top: 0,
    zIndex: 5,
    background: "rgba(4, 12, 9, .97)",
    display: "flex",
    gap: 10,
    alignItems: "center",
    flexWrap: "wrap",
    padding: "12px 14px",
    border: "1px solid rgba(52,211,153,.28)",
    color: "#fff",
    marginBottom: 14,
  },
  title: { fontWeight: 900, fontSize: 22, color: "#f7b955", letterSpacing: ".06em", textTransform: "uppercase" },
  subtitle: { fontSize: 11, color: "#78a28e", letterSpacing: ".04em" },
  input: { padding: "8px 10px", borderRadius: 2, border: "1px solid #315442", background: "#07110c", color: "#d9fbe8" },
  btn: {
    padding: "8px 12px",
    borderRadius: 2,
    border: "1px solid #49705c",
    background: "#10251a",
    color: "#d9fbe8",
    cursor: "pointer",
  },
  seg: { display: "inline-flex", gap: 8, alignItems: "center" },
  segBtn: (active) => ({
    padding: "6px 10px",
    borderRadius: 999,
    border: "1px solid rgba(255,255,255,.18)",
    background: active ? "rgba(255,255,255,.14)" : "transparent",
    color: "#eaf4ed",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 800,
  }),
  card: {
    background: "#0b1e13",
    borderRadius: 3,
    padding: 12,
    marginBottom: 14,
    border: "1px solid rgba(52,211,153,.22)",
    color: "#eaf4ed",
  },
  muted: { color: "rgba(255,255,255,.7)" },
  tbl: { width: "100%", borderCollapse: "collapse" },
  th: {
    textAlign: "left",
    padding: "7px 8px",
    borderBottom: "1px solid rgba(247,185,85,.35)",
    fontSize: 11,
    letterSpacing: ".08em",
    textTransform: "uppercase",
    color: "#f7b955",
    background: "#0b1710",
    whiteSpace: "nowrap",
  },
  td: {
    padding: "8px",
    borderBottom: "1px solid rgba(255,255,255,.08)",
    fontSize: 12,
    color: "#eaf4ed",
    verticalAlign: "top",
  },
  pill: (tone = "neutral") => {
    const base = {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "2px 8px",
      borderRadius: 999,
      fontSize: 11,
      border: "1px solid rgba(255,255,255,.14)",
      whiteSpace: "nowrap",
    };
    if (tone === "up") return { ...base, color: "#1db954", borderColor: "rgba(29,185,84,.5)" };
    if (tone === "down") return { ...base, color: "#ff6b6b", borderColor: "rgba(255,107,107,.5)" };
    return { ...base, color: "#eaf4ed" };
  },
  rowBtn: {
    width: "100%",
    textAlign: "left",
    background: "transparent",
    border: "none",
    padding: 0,
    margin: 0,
    color: "inherit",
    cursor: "pointer",
  },
  mini: { fontSize: 12, opacity: 0.85 },
  timelineWrap: {
    marginTop: 8,
    padding: 10,
    borderRadius: 2,
    background: "rgba(255,255,255,.05)",
    border: "1px solid rgba(255,255,255,.08)",
  },
  subCard: {
    marginTop: 10,
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,.10)",
    overflow: "hidden",
  },
};

// ---------- page ----------
export default function OddsMovementPage() {
  const [day, setDay] = useState(todayISO());
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [mode, setMode] = useState("top"); // "top" | "all"
  const [rows, setRows] = useState([]); // movers OR all-lines rows
  const [q, setQ] = useState(""); // filter

  // Expand is now per FIXTURE (not per line)
  const [expandedFixtureId, setExpandedFixtureId] = useState(null);

  // For expanded fixture timeline (per line inside that fixture)
  const [expandedLineKey, setExpandedLineKey] = useState(null); // `${fixtureId}_${book}_${market}_${selection}`
  const [detailsByKey, setDetailsByKey] = useState({}); // per-line timeline cache

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    setExpandedFixtureId(null);
    setExpandedLineKey(null);
    setDetailsByKey({});

    try {
      if (mode === "top") {
        const params = { day, hours: 36, min_pts: 0.1 };
        const { data } = await firstWorkingGet(TOP_MOVERS_ENDPOINTS, params);
        const list = Array.isArray(data) ? data : data?.movers || data?.rows || [];
        setRows(Array.isArray(list) ? list : []);
      } else {
        const params = { day, hours: 36, limit: 1200, sport: "football" };
        const { data } = await firstWorkingGet(CARD_ENDPOINTS, params);
        const list = Array.isArray(data) ? data : data?.rows || [];
        setRows(Array.isArray(list) ? list : []);
      }
    } catch (e) {
      setRows([]);
      setErr(String(e?.response?.data?.detail || e?.message || e));
    } finally {
      setLoading(false);
    }
  }, [day, mode]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const needle = (q || "").trim().toLowerCase();
    if (!needle) return rows;

    return rows.filter((r) => {
      const fx = `${r.home_team || ""} ${r.away_team || ""}`.toLowerCase();
      const mk = String(r.market || "").toLowerCase();
      const bm = String(r.bookmaker || "").toLowerCase();
      return fx.includes(needle) || mk.includes(needle) || bm.includes(needle);
    });
  }, [rows, q]);

  // Group lines by fixture_id for "all" mode
  const groupedByFixture = useMemo(() => {
    const by = {};
    for (const r of filtered) {
      const id = r.fixture_id;
      if (!by[id]) by[id] = [];
      by[id].push(r);
    }
    return by;
  }, [filtered]);

  // Summary rows for fixture-level table (works in BOTH modes)
  const fixtureSummaries = useMemo(() => {
    // if mode === "top", we might have 1 line per fixture (or many) — still group it.
    const summaries = Object.values(groupedByFixture).map((lines) => {
      const base = lines[0];

      // Normalize for movers endpoint: it returns first/last (not open_price/current_price)
      const norm = lines.map((l) => {
        const open_price = safeNum(l.open_price) ?? safeNum(l.first) ?? safeNum(l.opening_price);
        const current_price = safeNum(l.current_price) ?? safeNum(l.last) ?? safeNum(l.price);
        const move = safeNum(l.move) ?? (open_price != null && current_price != null ? current_price - open_price : null);

        return {
          ...l,
          open_price,
          current_price,
          move,
        };
      });

      const summary = summarizeFixture(norm, 0.1);

      const books = [...new Set(norm.map((x) => x.bookmaker).filter(Boolean))];
      const bookmaker = books.length === 1 ? books[0] : books.length ? "mixed" : "—";

      return {
        fixture_id: base.fixture_id,
        sport: base.sport,
        kickoff_utc: base.kickoff_utc,
        home_team: base.home_team,
        away_team: base.away_team,
        comp: base.comp,
        bookmaker,
        lines: norm, // keep for expand
        ...summary,
      };
    });

    summaries.sort((a, b) => String(a.kickoff_utc).localeCompare(String(b.kickoff_utc)));
    return summaries;
  }, [groupedByFixture]);

  const tapeStats = useMemo(() => {
    const moves = fixtureSummaries.flatMap((fixture) => fixture.lines.map((line) => Number(line.move)).filter(Number.isFinite));
    return {
      fixtures: fixtureSummaries.length,
      lines: fixtureSummaries.reduce((total, fixture) => total + fixture.linesCount, 0),
      movers: fixtureSummaries.reduce((total, fixture) => total + fixture.moversCount, 0),
      average: moves.length ? moves.reduce((total, move) => total + Math.abs(move), 0) / moves.length : 0,
    };
  }, [fixtureSummaries]);

  const toggleExpandFixture = (fixtureId) => {
    setExpandedFixtureId((cur) => (cur === fixtureId ? null : fixtureId));
    setExpandedLineKey(null);
  };

  const toggleExpandLine = async (line) => {
    const fixtureId = line.fixture_id;
    const key = `${fixtureId}_${line.bookmaker}_${line.market}_${line.selection || ""}`;
    const next = expandedLineKey === key ? null : key;
    setExpandedLineKey(next);
    if (!next) return;

    if (detailsByKey[key]?.history) return;

    setDetailsByKey((p) => ({ ...p, [key]: { loading: true, err: "", history: null } }));

    try {
      const params = {
        market: line.market,
        bookmaker: line.bookmaker,
        selection: line.selection || null,
        limit: 300,
      };

      const { data } = await firstWorkingGet(
        HISTORY_ENDPOINTS.map((p) => `${p}/${fixtureId}`),
        params
      );

      // backend returns grouped histories
      const groups = data?.groups || [];
      const match = groups.find(
        (g) =>
          String(g.bookmaker) === String(line.bookmaker) &&
          String(g.market) === String(line.market) &&
          String(g.selection || "") === String(line.selection || "")
      );
      const hist = match?.history || [];

      setDetailsByKey((p) => ({ ...p, [key]: { loading: false, err: "", history: hist } }));
    } catch (e) {
      setDetailsByKey((p) => ({
        ...p,
        [key]: {
          loading: false,
          err: String(e?.response?.data?.detail || e?.message || e),
          history: [],
        },
      }));
    }
  };

  return (
    <div style={S.page}>
      <div style={S.headerRow}>
        <div>
          <div style={S.title}>Movement Tracker</div>
          <div style={S.subtitle}>Odds movement across the card (opening → current). Expand a fixture to view lines.</div>
        </div>

        <div style={{ marginLeft: "auto" }} />

        <div style={S.seg}>
          <button style={S.segBtn(mode === "top")} onClick={() => setMode("top")} disabled={loading}>
            Top Movers
          </button>
          <button style={S.segBtn(mode === "all")} onClick={() => setMode("all")} disabled={loading}>
            All Lines
          </button>
        </div>

        <label style={{ color: "#eaf4ed", fontSize: 13 }}>
          Day:&nbsp;
          <input style={S.input} type="date" value={day} onChange={(e) => setDay(e.target.value)} />
        </label>

        <button style={S.btn} onClick={load} disabled={loading}>
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 1, marginBottom: 10, border: "1px solid #274b38", background: "#274b38" }}>
        {[["Fixtures", tapeStats.fixtures], ["Live lines", tapeStats.lines], ["Material movers", tapeStats.movers], ["Avg |delta|", tapeStats.average.toFixed(3)]].map(([label, value]) => (
          <div key={label} style={{ background: "#06110b", padding: "10px 12px" }}>
            <div style={{ color: "#718f7f", fontSize: 10, letterSpacing: ".1em", textTransform: "uppercase" }}>{label}</div>
            <strong style={{ color: label === "Material movers" ? "#f7b955" : "#34d399", fontSize: 20 }}>{value}</strong>
          </div>
        ))}
      </div>

      <div style={S.card}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <div style={{ fontWeight: 800, fontSize: 16 }}>
            {mode === "top" ? "Top Movers" : "All Lines"} <span style={S.muted}>• Football only for now</span>
          </div>

          <div style={{ marginLeft: "auto", minWidth: 260 }}>
            <input
              style={{ ...S.input, width: "100%" }}
              placeholder="Filter: team / market / bookmaker…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
        </div>

        <div style={{ marginTop: 10, fontSize: 12, opacity: 0.85 }}>
          Tip: Movement in your favour = value gets better (e.g. model likes 2.40, market drifts 2.60).
        </div>

        {err && <div style={{ marginTop: 10, color: "#ffb3b3" }}>{err}</div>}

        {loading ? (
          <div style={{ marginTop: 12 }}>Loading…</div>
        ) : !fixtureSummaries.length ? (
          <div style={{ marginTop: 12, ...S.muted }}>No rows found for this day/window.</div>
        ) : (
          <div style={{ overflowX: "auto", marginTop: 12 }}>
            <table style={S.tbl}>
              <thead>
                <tr>
                  <th style={S.th}>KO (UK)</th>
                  <th style={S.th}>Fixture</th>
                  <th style={S.th}>Book</th>
                  <th style={S.th}>Lines</th>
                  <th style={S.th}>Movers</th>
                  <th style={S.th}>Biggest</th>
                  <th style={S.th}></th>
                </tr>
              </thead>
              <tbody>
                {fixtureSummaries.map((fx) => {
                  const isOpen = expandedFixtureId === fx.fixture_id;
                  const big = fx.biggest;
                  const bigTone =
                    big && typeof big.open === "number" && typeof big.cur === "number"
                      ? movementTone(big.open, big.cur)
                      : "neutral";

                  return (
                    <React.Fragment key={`fx_${fx.fixture_id}`}>
                      {/* --- FIXTURE SUMMARY ROW --- */}
                      <tr>
                        <td style={S.td}>{toUK(fx.kickoff_utc)}</td>

                        <td style={S.td}>
                          <button style={S.rowBtn} onClick={() => toggleExpandFixture(fx.fixture_id)}>
                            <div style={{ fontWeight: 900 }}>
                              {fx.home_team} <span style={{ opacity: 0.6 }}>vs</span> {fx.away_team}
                            </div>
                            <div style={S.mini}>
                              <Link
                                to={routeForFixture(fx.fixture_id, fx.sport)}
                                style={{ color: "#9be7ff", textDecoration: "none" }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                Open fixture →
                              </Link>
                              <span style={{ marginLeft: 10, opacity: 0.75 }}>{isOpen ? "▲" : "▼"}</span>
                            </div>
                          </button>
                        </td>

                        <td style={S.td}>{fx.bookmaker}</td>
                        <td style={S.td}>{fx.linesCount}</td>
                        <td style={S.td}>{fx.moversCount}</td>

                        <td style={S.td}>
                          {big ? (
                            <span style={S.pill(bigTone)}>
                              {big.market} {big.move > 0 ? `+${big.move.toFixed(2)}` : big.move.toFixed(2)}
                            </span>
                          ) : (
                            <span style={S.muted}>—</span>
                          )}
                        </td>

                        <td style={S.td}>
                          <button style={S.btn} onClick={() => toggleExpandFixture(fx.fixture_id)}>
                            {isOpen ? "Hide lines" : "View lines"}
                          </button>
                        </td>
                      </tr>

                      {/* --- EXPANDED LINES (per fixture) --- */}
                      {isOpen && (
                        <tr>
                          <td colSpan={7} style={{ ...S.td, paddingTop: 0 }}>
                            <div style={S.subCard}>
                              <div style={{ overflowX: "auto" }}>
                                <table style={S.tbl}>
                                  <thead>
                                    <tr>
                                      <th style={S.th}>Market</th>
                                      <th style={S.th}>Book</th>
                                      <th style={S.th}>Open</th>
                                      <th style={S.th}>Now</th>
                                      <th style={S.th}>Δ</th>
                                      <th style={S.th}>Move %</th>
                                      <th style={S.th}></th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {fx.lines.map((l) => {
                                      const open = safeNum(l.open_price);
                                      const cur = safeNum(l.current_price);
                                      const d = delta(open, cur);
                                      const movePct =
                                        typeof open === "number" && typeof cur === "number" && open > 0
                                          ? (cur - open) / open
                                          : null;

                                      const tone = movementTone(open, cur);
                                      const lineKey = `${l.fixture_id}_${l.bookmaker}_${l.market}_${l.selection || ""}`;
                                      const lineOpen = expandedLineKey === lineKey;

                                      return (
                                        <React.Fragment key={lineKey}>
                                          <tr>
                                            <td style={S.td}>
                                              <span style={S.pill("neutral")}>
                                                {l.market}
                                                {l.selection ? (
                                                  <span style={{ opacity: 0.85 }}>• {l.selection}</span>
                                                ) : null}
                                              </span>
                                            </td>

                                            <td style={S.td}>{l.bookmaker}</td>
                                            <td style={S.td}>{open == null ? "—" : num(open, 2)}</td>
                                            <td style={S.td}>
                                              <span style={S.pill(tone)}>{cur == null ? "—" : num(cur, 2)}</span>
                                            </td>
                                            <td
                                              style={{
                                                ...S.td,
                                                color:
                                                  tone === "up"
                                                    ? "#1db954"
                                                    : tone === "down"
                                                    ? "#ff6b6b"
                                                    : "#eaf4ed",
                                              }}
                                            >
                                              {d === null ? "—" : d > 0 ? `+${d.toFixed(2)}` : d.toFixed(2)}
                                            </td>
                                            <td style={S.td}>
                                              {movePct === null ? "—" : `${(movePct * 100).toFixed(1)}%`}
                                            </td>
                                            <td style={S.td}>
                                              <button style={S.btn} onClick={() => toggleExpandLine(l)}>
                                                {lineOpen ? "Hide" : "Timeline"}
                                              </button>
                                            </td>
                                          </tr>

                                          {lineOpen && (
                                            <tr>
                                              <td colSpan={7} style={{ ...S.td, paddingTop: 0 }}>
                                                <div style={S.timelineWrap}>
                                                  <MovementTimeline
                                                    itemKey={lineKey}
                                                    details={detailsByKey[lineKey]}
                                                    kickoffUtc={fx.kickoff_utc}
                                                  />
                                                </div>
                                              </td>
                                            </tr>
                                          )}
                                        </React.Fragment>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div style={S.card}>
        <div style={{ fontWeight: 800, marginBottom: 6 }}>How this works</div>
        <div style={{ fontSize: 13, opacity: 0.9, lineHeight: 1.5 }}>
          We store immutable snapshots only when the price changes. If you ingest the night before and again in the
          morning, you’ll get opening vs current movement automatically.
        </div>
      </div>
    </div>
  );
}

// ---------- timeline subcomponent ----------
function MovementTimeline({ itemKey, details, kickoffUtc }) {
  const num = (x, d = 2) => (typeof x === "number" && isFinite(x) ? x.toFixed(d) : "—");

  const toUK = (iso, { withZone = false } = {}) => {
    if (!iso) return "—";
    const fmt = { timeZone: "Europe/London", hour: "2-digit", minute: "2-digit" };
    if (withZone) fmt.timeZoneName = "short";
    try {
      const d = new Date(iso);
      if (!isNaN(d)) return d.toLocaleTimeString("en-GB", fmt);
    } catch {}
    try {
      const safe = iso.endsWith("Z") || iso.includes("+") ? iso : iso + "Z";
      return new Date(safe).toLocaleTimeString("en-GB", fmt);
    } catch {
      return iso.slice(11, 16);
    }
  };

  if (!details) return <div style={{ opacity: 0.85 }}>Loading movement…</div>;
  if (details.loading) return <div style={{ opacity: 0.85 }}>Loading movement…</div>;
  if (details.err) return <div style={{ color: "#ffb3b3" }}>{details.err}</div>;

  const history = Array.isArray(details.history) ? details.history : [];
  if (!history.length) return <div style={{ opacity: 0.8 }}>No snapshots yet for this row.</div>;

  const rows = [...history]
    .map((h) => ({
      price: typeof h.price === "number" ? h.price : Number(h.price),
      captured_at: h.captured_at || h.capturedAt || h.at,
    }))
    .filter((x) => isFinite(x.price))
    .sort((a, b) => String(a.captured_at).localeCompare(String(b.captured_at)));

  const open = rows[0]?.price;
  const now = rows[rows.length - 1]?.price;
  const diff = typeof open === "number" && typeof now === "number" ? now - open : null;

  return (
    <div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ fontWeight: 900 }}>Timeline</div>
        <div style={{ fontSize: 12, opacity: 0.85 }}>
          {rows.length} snapshots • KO {toUK(kickoffUtc, { withZone: true })}
        </div>
        <div style={{ marginLeft: "auto", fontSize: 12, opacity: 0.9 }}>
          Open <b>{num(open, 2)}</b> → Now <b>{num(now, 2)}</b>{" "}
          {diff === null ? (
            ""
          ) : (
            <span style={{ marginLeft: 8, color: diff > 0 ? "#1db954" : diff < 0 ? "#ff6b6b" : "#eaf4ed" }}>
              ({diff > 0 ? "+" : ""}
              {diff.toFixed(2)})
            </span>
          )}
        </div>
      </div>

      <div style={{ marginTop: 10, overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left", fontSize: 12, padding: "6px 4px", opacity: 0.8 }}>Captured</th>
              <th style={{ textAlign: "left", fontSize: 12, padding: "6px 4px", opacity: 0.8 }}>Price</th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(-20).map((r, i) => (
              <tr key={`${itemKey}_${i}`}>
                <td style={{ padding: "6px 4px", fontSize: 12, opacity: 0.9 }}>
                  {r.captured_at ? new Date(r.captured_at).toLocaleString("en-GB") : "—"}
                </td>
                <td style={{ padding: "6px 4px", fontSize: 12, fontWeight: 900 }}>{num(r.price, 2)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {rows.length > 20 && (
          <div style={{ marginTop: 6, fontSize: 12, opacity: 0.8 }}>Showing last 20 snapshots.</div>
        )}
      </div>
    </div>
  );
}
